import { Routes, Route } from 'react-router-dom';
import React, { Suspense, lazy, useEffect, useState } from 'react';
import {
  ProtectedRoute,
  UserRoute,
  EditorRoute,
  ReviewerRoute,
  AdminRoute
} from '../components/routes/RouteGuards';

import Spinner from '../components/common/Spinner';
import MainLayout from '../components/layout/MainLayout';

import Disclaimer from '../pages/Disclaimer.jsx';
import Terms from '../pages/TermsPage.jsx';
import Privacy from '../pages/Privacy.jsx';
import Cookies from '../pages/Cookies.jsx';
import Accessibility from '../pages/Accessibility.jsx';
import Licensing from '../pages/Licensing.jsx';
import EditorialIndependence from '../pages/EditorialIndependence.jsx';
import ComplaintsAppeals from '../pages/ComplaintsAppeals.jsx';
import DMCA from '../pages/DMCA.jsx';
import IndexingDOI from '../pages/IndexingDOI.jsx';
import OrcidCrossrefCope from '../pages/OrcidCrossrefCope.jsx';
import CopyrightLicensingPolicy from '../pages/CopyrightLicensingPolicy.jsx';
import Ethics from '../pages/Ethics.jsx';
import AdvisorsPage from "../pages/AdvisorsPage.jsx";
import Policy from '../pages/Policy.jsx';
import AIInMedicine from '../pages/AIInMedicine.js';
import JoinBoard from '../pages/JoinBoard.jsx';

// Lazy loaded components
const HomePage = lazy(() => import('../pages/HomePage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));
const ProfilePage = lazy(() => import('../pages/auth/ProfilePage'));
const EditProfilePage = lazy(() => import('../pages/auth/EditProfilePage'));
const ArticlesPage = lazy(() => import('../pages/article/ArticlesPage'));
const CommonArticlesPage = lazy(() => import('../pages/article/CommonArticlesPage.jsx'));
const PaymentPage = lazy(() => import('../pages/payment/PaymentPage.jsx'));
const PlanPage = lazy(() => import('../pages/payment/PlanPage.jsx'));
const ArticleDetailsPage = lazy(() => import('../pages/article/ArticleDetailsPage'));
const AddArticlePage = lazy(() => import('../pages/article/AddArticlePage'));
const EditArticlePage = lazy(() => import('../pages/article/EditArticlePage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage'));
const ReviewerPage = lazy(() => import('../pages/reviewer/ReviewerPage'));
const ReviewPage = lazy(() => import('../pages/reviewer/ReviewPage'));
const ReviewArticlePage = lazy(() => import('../pages/reviewer/ReviewArticlePage'));
const AboutPage = lazy(() => import('../pages/AboutPage'));
const ContactPage = lazy(() => import('../pages/ContactPage'));
const JournalDetailsPage = lazy(() => import('../pages/journal/JournalDetailsPage'));
const AddJournalPage = lazy(() => import('../pages/journal/AddJournalPage'));
const EditJournalPage = lazy(() => import('../pages/journal/EditJournalPage'));
const AddEditorPage = lazy(() => import('../pages/journal/AddEditorPage'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const Boarding = lazy(() => import('../pages/Boarding'));

/**
 * A suspense fallback that:
 * - shows immediately when a lazy chunk is loading
 * - stays visible for a minimum duration (prevents 0ms "blink" / not showing on fast refresh)
 * - respects fixed navbar spacing (pt-20)
 */
const LoadingSpinner = () => {
  const MIN_MS = 450; // small, feels deliberate (adjust 300–600 if you want)
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShow(false), MIN_MS);
    return () => clearTimeout(t);
  }, []);

  // Keep a clean full-page centered loader (AJMM theme already handled in Spinner)
  // We intentionally do NOT hide instantly; even if load finishes quickly, this avoids "no spinner" feeling.
  return (
    <div className="pt-20">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-7xl items-center justify-center px-4">
        <div className="flex w-full max-w-md flex-col items-center gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40">
            <Spinner />
            <div className="mt-3 text-center">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Loading…
              </p>
              <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Preparing your AJMM experience
              </p>
            </div>
          </div>
          {/* subtle space (no logic impact) */}
          {show ? null : null}
        </div>
      </div>
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Routes with MainLayout */}
      <Route element={<MainLayout />}>
        <Route path="/policies/:type" element={<Policy />} />

        {/* Public routes */}
        <Route
          path="/disclaimer"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Disclaimer />
            </Suspense>
          }
        />

        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <LoginPage />
            </Suspense>
          }
        />

        <Route
          path="/register"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <RegisterPage />
            </Suspense>
          }
        />

        <Route
          path="/verify-email"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <VerifyEmailPage />
            </Suspense>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />

        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />

        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Terms />
            </Suspense>
          }
        />

        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Privacy />
            </Suspense>
          }
        />

        <Route
          path="/cookies"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Cookies />
            </Suspense>
          }
        />

        <Route
          path="/accessibility"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Accessibility />
            </Suspense>
          }
        />

        <Route
          path="/licensing"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Licensing />
            </Suspense>
          }
        />

        <Route
          path="/editorialIndependence"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <EditorialIndependence />
            </Suspense>
          }
        />

        <Route
          path="/complaintsAppeals"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ComplaintsAppeals />
            </Suspense>
          }
        />

        <Route
          path="/dmca"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <DMCA />
            </Suspense>
          }
        />

        <Route path="/ai-in-medicine" element={<AIInMedicine />} />
        {/* <Route path="/boarding" element={<JoinBoard />} /> */}

        <Route
          path="/indexingDOI"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <IndexingDOI />
            </Suspense>
          }
        />

        <Route
          path="/orcidCrossrefCope"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <OrcidCrossrefCope />
            </Suspense>
          }
        />

        <Route
          path="/copyrightLicensingPolicy"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <CopyrightLicensingPolicy />
            </Suspense>
          }
        />

        <Route
          path="/ethics"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Ethics />
            </Suspense>
          }
        />

        <Route
          path="/"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <HomePage />
            </Suspense>
          }
        />

        <Route
          path="/about"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <AboutPage />
            </Suspense>
          }
        />

        <Route
          path="/boarding"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <Boarding />
            </Suspense>
          }
        />

        <Route
          path="/advisors"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdvisorsPage />
            </Suspense>
          }
        />

        <Route
          path="/contact"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ContactPage />
            </Suspense>
          }
        />

        <Route
          path="/plans"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <PlanPage />
            </Suspense>
          }
        />

        {/* 404 page with MainLayout */}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <NotFoundPage />
            </Suspense>
          }
        />

        {/* Protected Journal Details route - requires authentication */}
        <Route
          path="/view-journal/:journalId"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <JournalDetailsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Edit Journal route - only for admins */}
        <Route
          path="/edit-journal/:journalId"
          element={
            <AdminRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <EditJournalPage />
              </Suspense>
            </AdminRoute>
          }
        />

        {/* Common protected routes - accessible by both roles */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ProfilePage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/edit-profile"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <EditProfilePage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* User-only routes */}
        <Route
          path="/add-article"
          element={
            <UserRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <AddArticlePage />
              </Suspense>
            </UserRoute>
          }
        />

        <Route
          path="/payment"
          element={
            <UserRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <PaymentPage />
              </Suspense>
            </UserRoute>
          }
        />

        <Route
          path="/articles/:articleId/edit"
          element={
            <UserRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <EditArticlePage />
              </Suspense>
            </UserRoute>
          }
        />

        {/* Shared article routes with conditional rendering inside */}
        <Route
          path="/articles"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ArticlesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/allArticlesPage"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <CommonArticlesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/articles/:articleId"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ArticleDetailsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        <Route
          path="/free-article/:articleId"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ArticleDetailsPage isFreePreview={true} />
            </Suspense>
          }
        />

        {/* Editor-only routes */}
        <Route
          path="/reviewer"
          element={
            <EditorRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ReviewerPage />
              </Suspense>
            </EditorRoute>
          }
        />

        {/* Reviewer-only routes */}
        <Route
          path="/review"
          element={
            <ReviewerRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ReviewPage />
              </Suspense>
            </ReviewerRoute>
          }
        />

        <Route
          path="/review/:articleId"
          element={
            <ReviewerRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <ReviewArticlePage />
              </Suspense>
            </ReviewerRoute>
          }
        />

        {/* Admin-only routes */}
        <Route
          path="/add-journal"
          element={
            <AdminRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <AddJournalPage />
              </Suspense>
            </AdminRoute>
          }
        />

        <Route
          path="/add-editor"
          element={
            <AdminRoute>
              <Suspense fallback={<LoadingSpinner />}>
                <AddEditorPage />
              </Suspense>
            </AdminRoute>
          }
        />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
